// Which racing codes this device deals from.
//
// Three switches in Settings, and the whole of what they mean is here: what the
// card is asked for, which races survive on the device, which sports a racing
// strategy may look at, and the one refusal — the last code stays on, because a
// Racing tab with no code is a tab with nothing to show and no way back.
//
// PURE, AND A LEAF. Nothing here reads storage, a clock or a store: a stored
// record goes in and a plain answer comes out, which is what lets the rule be
// tested without a device. `store.js` holds the record and is the only thing
// that writes it, exactly as `nextHidden` is pure and `setShown` saves.
//
// THE KEY IS PUNTERSEDGE'S CATEGORY — `horse`, not `horses`. It is the word
// `categories=` carries on the wire and the word `SPORT_FOR_CATEGORY` is keyed
// on, so a preference a phone keeps for a year is in the same vocabulary the
// feed speaks. `view.js`'s `code` is the word on the Racing tab's chip
// ("horses") and its `noun` is this one.

import { SPORT_FOR_CATEGORY } from './board.js';
import { sportLabel } from '../engine/teams.js';

/** The three, in the order Settings prints them and the card is asked for them. */
export const RACING_CODE_KEYS = Object.freeze(['horse', 'greyhound', 'harness']);

/** Where the three switches are remembered. Beside `pref.racingCode`, which is
 *  a different setting: that one is which code the tab is LOOKING at. */
export const RACING_CODES_PREF = 'pref.racingCodes';

/** All three on. The app as shipped: a punter who has set nothing gets the card. */
export const DEFAULT_CODES = Object.freeze({ horse: true, greyhound: true, harness: true });

/** "Horses", "Greyhounds", "Harness" — the sport's own name, never a second one. */
export const codeLabel = (code) => sportLabel(SPORT_FOR_CATEGORY[code]);

/**
 * A stored record as the app reads it: exactly three booleans.
 *
 * Junk, a missing key and a missing record all come back as all-on, and so does
 * a record with all three off. A device cannot arrive at that one through
 * `setCode` — the refusal below is what stops it — so a stored all-off is a
 * record from somewhere else, and the honest reading of it is the shipped one
 * rather than a card that is permanently empty with no clue why.
 */
export function normaliseCodes(stored) {
  if (!stored || typeof stored !== 'object') return { ...DEFAULT_CODES };
  const codes = {};
  for (const key of RACING_CODE_KEYS) codes[key] = stored[key] !== false;
  return Object.values(codes).some(Boolean) ? codes : { ...DEFAULT_CODES };
}

/** The codes that are on, in the fixed order. Never empty. */
export function enabledCodes(stored) {
  const codes = normaliseCodes(stored);
  return RACING_CODE_KEYS.filter((key) => codes[key]);
}

/** The codes that are OFF, in the same order. Empty when all three are on. */
export function disabledCodes(stored) {
  const codes = normaliseCodes(stored);
  return RACING_CODE_KEYS.filter((key) => !codes[key]);
}

/**
 * The record after switching one code on or off, and why it was refused if it
 * was: `{ codes, refused: 'last' | 'unknown' | null }`.
 *
 * The same two rules and the same shape as `nextHidden` in
 * `lib/stores/strategies.js`, for the same reasons:
 *
 *   'unknown'  a word that is not one of the three. Storing it would put a key
 *              in the record that no switch can ever take out again.
 *   'last'     it would leave the card with no code at all. Switching the last
 *              one off is not a preference, it is switching the tab off, and
 *              the caller says so rather than letting a toggle spring back with
 *              no explanation.
 *
 * Switching one ON is never refused. Idempotent, and it never touches the
 * record it was given.
 */
export function setCode(stored, code, on) {
  const codes = normaliseCodes(stored);
  const key = String(code ?? '');
  if (!RACING_CODE_KEYS.includes(key)) return { codes, refused: 'unknown' };
  if (on) return { codes: { ...codes, [key]: true }, refused: null };
  // Already off: nothing changes, so there is nothing to refuse — before the
  // count below, which would otherwise answer 'last' to a tap that was not
  // going to do anything anyway.
  if (!codes[key]) return { codes, refused: null };
  if (enabledCodes(codes).length === 1) return { codes, refused: 'last' };
  return { codes: { ...codes, [key]: false }, refused: null };
}

/** The sport ids the enabled codes stand for: `['racing_horse', …]`. */
export function codesToSports(stored) {
  return enabledCodes(stored).map((code) => SPORT_FOR_CATEGORY[code]);
}

/**
 * What goes on the wire as `categories=`, or null for "all of them".
 *
 * Null rather than the three words when nothing is switched off, so a device
 * that has never touched these switches sends exactly the request it always
 * has — and it is one pull either way. The upstream drops the parameter at the
 * same point for the same reason (`categoriesParam`, server/providers).
 */
export function codesParam(stored) {
  const wanted = enabledCodes(stored);
  return wanted.length === RACING_CODE_KEYS.length ? null : wanted;
}

/**
 * A card's races, less the codes that are switched off.
 *
 * Applied to the RAW races rather than to the board they fold into, because a
 * cached card was bought before the switch was thrown: the device can be
 * holding greyhounds it must no longer deal from, and dropping them here is
 * what keeps every screen, strategy and notification reading one card.
 */
export function racesForCodes(races, stored) {
  const wanted = enabledCodes(stored);
  return (Array.isArray(races) ? races : [])
    .filter((race) => wanted.includes(String(race?.category ?? '').toLowerCase()));
}

/**
 * What the Racing tab's dateline says about the codes that are not on it:
 * "Harness is off in Settings", or nothing at all when all three are on.
 *
 * A dateline rather than a banner, and a fact rather than a warning: the reader
 * asked for this, and the only thing they need is somewhere to look when the
 * card is shorter than they expected.
 */
export function codesOffLine(stored) {
  const off = disabledCodes(stored).map(codeLabel);
  if (!off.length) return '';
  const named = off.length === 1 ? off[0] : `${off.slice(0, -1).join(', ')} and ${off.at(-1)}`;
  return `${named} ${off.length === 1 ? 'is' : 'are'} off in Settings`;
}

/** The sentence a refused switch prints: the last code on, and why it stays. */
export function lastCodeLine(stored) {
  return `${codeLabel(enabledCodes(stored)[0])} stays on: the card needs at least one code.`;
}
